import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ClipboardCheck, Sparkles, FileText, Users, Building2, CreditCard } from "lucide-react";

const services = [
  {
    icon: ClipboardCheck,
    title: "Food Safety Audits",
    desc: "Detailed kitchen and operational audits with structured findings, risk observations and practical action recommendations.",
    large: true,
  },
  {
    icon: Sparkles,
    title: "Kitchen Hygiene Checks",
    desc: "Reviews of cleanliness, sanitation routines, waste handling, pest-risk areas and day-to-day hygiene discipline.",
    large: false,
  },
  {
    icon: FileText,
    title: "Food Handling & Storage Review",
    desc: "Assessment of receiving, storage, labeling, temperature discipline, stock rotation and contamination control practices.",
    large: false,
  },
  {
    icon: Users,
    title: "Staff Hygiene Checks",
    desc: "Evaluation of grooming, handwashing, glove use, personal hygiene and overall food-safe behavior by kitchen teams.",
    large: false,
  },
  {
    icon: Building2,
    title: "Compliance Support",
    desc: "Gap identification, pre-inspection readiness and corrective guidance to help operators improve compliance confidence.",
    large: false,
  },
  {
    icon: CreditCard,
    title: "FSSAI License Support",
    desc: "Help in getting the FSSAI license for restaurants, cafes, food manufacturing units, bakeries and other food establishments.",
    large: true,
  },
];

export default function Services() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="services" className="py-12 md:py-20 bg-white" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#7CA63A] mb-4 block">What We Do</span>
          <h2 className="font-['Outfit'] text-2xl sm:text-3xl lg:text-4xl font-bold text-[#16231A] tracking-tight leading-[1.2] mb-4">
            Our Services
          </h2>
          <p className="text-[#526B57] max-w-2xl mx-auto leading-relaxed">
            Built for restaurants, cafes, cloud kitchens, bakeries, central kitchens and food manufacturing units that want stronger hygiene systems.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 25 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * i }}
              className={`group bg-[#F7F8F5] border border-[#E3E8DC] rounded-2xl p-8 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-all duration-300 ${
                s.large ? "lg:col-span-2 md:col-span-2" : ""
              }`}
              data-testid={`service-card-${i}`}
            >
              {s.large && (
                <div className="mb-4 rounded-xl overflow-hidden h-40">
                  <img
                    src={i === 0
                      ? "https://images.unsplash.com/photo-1771574207826-02f0d845e3a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwzfHxjb21tZXJjaWFsJTIwa2l0Y2hlbiUyMGNoZWYlMjBjbGVhbnxlbnwwfHx8fDE3NzU4OTQ0ODR8MA&ixlib=rb-4.1.0&q=85"
                      : "https://images.unsplash.com/photo-1665088127661-83aeff6104c4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGZvb2QlMjBpbmdyZWRpZW50cyUyMG9yZ2FuaWN8ZW58MHx8fHwxNzc1ODk0NDg5fDA&ixlib=rb-4.1.0&q=85"
                    }
                    alt={s.title}
                    className="w-full h-full object-cover rounded-xl group-hover:scale-[1.02] transition-transform duration-500"
                  />
                </div>
              )}
              <div className="w-10 h-10 bg-[#EAF0E2] rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#7CA63A] transition-colors duration-300">
                <s.icon size={20} className="text-[#7CA63A] group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="font-['Outfit'] text-lg font-bold text-[#16231A] mb-2">{s.title}</h3>
              <p className="text-sm text-[#526B57] leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
